"use client";

import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@speakai/ui/button";
import { Card } from "@speakai/ui/card";
import { Switch } from "@speakai/ui/switch";
import { Combobox } from "@/components/shared/combobox";
import { Badge } from "@speakai/ui/badge";
import { Tooltip } from "@speakai/ui/tooltip";
import { FileHeadphone, FilePlay, FileText, CircleCheck, RotateCw, X, Lock, Link2, Info } from "lucide-react";
import { formatFileSize, formatDuration } from "@/lib/utils/format";
import { cn } from "@/lib/utils/cn";
import { Img } from "@/components/shared/img";
import { detectPlatform } from "@/features/upload/url-validators";
import { useCredits, formatCredits } from "@/features/subscription/hooks/use-credits";
import type { UploadFileItem } from "@/features/upload/hooks/use-upload-validation";
import type { UrlImportItem } from "@/features/upload/hooks/use-url-import";
import type { CreditEstimateItemResult } from "@/features/upload/hooks/use-credit-estimate";

/** Per-file row-level translate error codes (set by useUploadValidation.validateBatch). */
type TranslateRowError =
  | "TARGET_LANGUAGE_REQUIRED"
  | "INVALID_TARGET_LANGUAGE"
  | "SOURCE_TARGET_SAME";

const TRANSLATE_ROW_ERROR_MESSAGE: Record<TranslateRowError, { i18nKey: string; fallback: string }> = {
  TARGET_LANGUAGE_REQUIRED: {
    i18nKey: "TRANSLATE.ERROR.TARGET_REQUIRED",
    fallback: "Choose a target language for this file",
  },
  INVALID_TARGET_LANGUAGE: {
    i18nKey: "TRANSLATE.ERROR.INVALID_TARGET",
    fallback: "Choose a valid target language",
  },
  SOURCE_TARGET_SAME: {
    i18nKey: "TRANSLATE.ERROR.SAME_LANG",
    fallback: "Source and target language must differ",
  },
};

/**
 * Internal row model. File and URL items render through the same shape so the
 * table has one set of columns + one rendering path. The `kind` discriminator
 * controls per-cell behavior (progress bar for files, link icon for URLs, etc.).
 */
interface FileRow {
  kind: "file";
  id: string;
  fileName: string;
  mediaCategory: "audio" | "video" | "text";
  sizeBytes: number;
  duration: number;
  amount: number;
  sourceLanguage: string;
  targetLanguage: string;
  folderId: string;
  enableDubbing: boolean;
  isUploaded: boolean;
  isValid: boolean;
  errorMessage?: string;
  percent: number;
}

interface UrlRow {
  kind: "url";
  id: string;
  fileName: string;
  mediaCategory: "audio" | "video";
  sizeBytes: number;
  duration: number;
  amount: number;
  sourceLanguage: string;
  targetLanguage: string;
  folderId: string;
  enableDubbing: boolean;
  isUploaded: boolean;
  /** Source platform, when the URL is one we have a mark for. */
  platform: { name: string; icon: string } | null;
}

type Row = FileRow | UrlRow;

interface FileListProps {
  files: UploadFileItem[];
  urlItems?: UrlImportItem[];
  folders: { folderId: string; name: string }[];
  /** Transcription (source) language options for the source-language column. */
  sourceLanguages?: ReadonlyArray<{ code: string; label: string }>;
  /** Translation (target) language options for the target-language column (translate mode only). */
  targetLanguages?: ReadonlyArray<{ code: string; label: string }>;
  /** When "translate", FileList shows target language + dubbing columns and gates them as required. */
  mode?: "upload" | "translate";
  onLanguageChange?: (fileId: string, lang: string) => void;
  onTargetLanguageChange?: (fileId: string, lang: string) => void;
  onFolderChange?: (fileId: string, folderId: string) => void;
  onDubbingChange?: (fileId: string, enabled: boolean) => void;
  onRemoveFile: (fileId: string) => void;
  onRetry?: (fileId: string) => void;
  onUrlLanguageChange?: (itemId: string, lang: string) => void;
  onUrlTargetLanguageChange?: (itemId: string, lang: string) => void;
  onUrlFolderChange?: (itemId: string, folderId: string) => void;
  onUrlDubbingChange?: (itemId: string, enabled: boolean) => void;
  onRemoveUrlItem?: (itemId: string) => void;
  disabled?: boolean;
  /** Per-row translate validation errors (set by validateBatch at submit time). Keyed by file or URL id. */
  perFileErrors?: Record<string, TranslateRowError | string>;
  /** Disables the dubbing toggle for trial users with $0 balance. */
  dubbingGateBlocked?: boolean;
  /** Per-row credit estimate (aligned to rows: files then urlItems), drives the Standard/Premium badge. */
  creditItems?: CreditEstimateItemResult[];
}

function getFileIcon(
  category: "audio" | "video" | "text",
  isUrl: boolean,
  platform?: { name: string; icon: string } | null,
) {
  // Brand marks are filled shapes, so at the lucide 16px they shrink to a speck; the tile gives them weight.
  if (isUrl && platform) {
    return (
      <span
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-background ring-1 ring-inset ring-border"
        title={platform.name}
      >
        <Img
          src={`/assets/media/icons/platforms/${platform.icon}.svg`}
          alt={platform.name}
          width={18}
          height={18}
          className={cn(
            "h-[18px] w-[18px] object-contain",
            platform.icon === "x-twitter" && "dark:invert"
          )}
          loading="lazy"
          data-testid={`upload-row-platform-${platform.icon}`}
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
      </span>
    );
  }
  if (isUrl) return <Link2 className="h-4 w-4 text-primary" aria-hidden="true" />;
  switch (category) {
    case "audio":
      return <FileHeadphone className="h-4 w-4 text-info" aria-hidden="true" />;
    case "video":
      return <FilePlay className="h-4 w-4 text-primary" aria-hidden="true" />;
    case "text":
      return <FileText className="h-4 w-4 text-warning" aria-hidden="true" />;
  }
}

function toFileRow(file: UploadFileItem): FileRow {
  return {
    kind: "file",
    id: file.id,
    fileName: file.fileName,
    mediaCategory: file.mediaCategory,
    sizeBytes: file.file.size,
    duration: file.duration,
    amount: file.amount,
    sourceLanguage: file.sourceLanguage,
    targetLanguage: file.targetLanguage,
    folderId: file.folderId,
    enableDubbing: file.enableDubbing,
    isUploaded: file.isUploaded,
    isValid: file.isValid,
    errorMessage: file.errorMessage,
    percent: file.percent,
  };
}

function toUrlRow(item: UrlImportItem): UrlRow {
  return {
    kind: "url",
    id: item.id,
    fileName: item.fileName,
    mediaCategory: item.mediaCategory,
    sizeBytes: item.size,
    duration: item.duration,
    amount: item.amount,
    sourceLanguage: item.sourceLanguage,
    targetLanguage: item.targetLanguage,
    folderId: item.folderId,
    enableDubbing: item.enableDubbing,
    isUploaded: !!item.isUploaded,
    platform: detectPlatform(item.originalUrl),
  };
}

function TierBadge({ tier, reason }: { tier: "standard" | "premium"; reason: string }) {
  return (
    <Tooltip content={<span className="block max-w-[220px] whitespace-normal text-left">{reason}</span>}>
      <span className="inline-flex w-fit items-center gap-1">
        <Badge variant={tier === "premium" ? "warning" : "secondary"} size="sm" className="capitalize cursor-default">
          {tier}
        </Badge>
        <Info className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden="true" />
      </span>
    </Tooltip>
  );
}

export function FileList({
  files,
  urlItems = [],
  folders,
  sourceLanguages = [],
  targetLanguages = [],
  mode = "upload",
  onLanguageChange = () => {},
  onTargetLanguageChange = () => {},
  onFolderChange = () => {},
  onDubbingChange = () => {},
  onRemoveFile,
  onRetry,
  onUrlLanguageChange = () => {},
  onUrlTargetLanguageChange = () => {},
  onUrlFolderChange = () => {},
  onUrlDubbingChange = () => {},
  onRemoveUrlItem = () => {},
  disabled = false,
  perFileErrors,
  dubbingGateBlocked = false,
  creditItems,
}: FileListProps) {
  const { t } = useTranslation();
  const { creditMode } = useCredits();
  const isTranslateMode = mode === "translate";

  const tierReason = (tier: "standard" | "premium", approximate: boolean): string =>
    tier === "premium"
      ? t(
          "UPLOAD_FILE.TIER_PREMIUM_REASON",
          "This language uses a higher-accuracy transcription engine, billed at a higher rate.",
        )
      : approximate
        ? t(
            "UPLOAD_FILE.TIER_STANDARD_APPROX_REASON",
            "The language is auto-detected; the final tier is confirmed after upload.",
          )
        : t("UPLOAD_FILE.TIER_STANDARD_REASON", "Standard transcription engine.");

  const rows: Row[] = [
    ...files.map(toFileRow),
    ...urlItems.map(toUrlRow),
  ];

  // The estimate items are positional (files then urlItems). Only trust the mapping
  // when the counts match exactly, otherwise fail closed to no badge rather than
  // showing the wrong file's tier.
  const tierItems =
    creditMode && creditItems && creditItems.length === rows.length ? creditItems : undefined;

  if (rows.length === 0) return null;

  const handleLanguageChange = (row: Row, lang: string) =>
    row.kind === "file" ? onLanguageChange(row.id, lang) : onUrlLanguageChange(row.id, lang);
  const handleTargetLanguageChange = (row: Row, lang: string) =>
    row.kind === "file" ? onTargetLanguageChange(row.id, lang) : onUrlTargetLanguageChange(row.id, lang);
  const handleFolderChange = (row: Row, folderId: string) =>
    row.kind === "file" ? onFolderChange(row.id, folderId) : onUrlFolderChange(row.id, folderId);
  const handleDubbingChange = (row: Row, enabled: boolean) =>
    row.kind === "file" ? onDubbingChange(row.id, enabled) : onUrlDubbingChange(row.id, enabled);
  const handleRemove = (row: Row) =>
    row.kind === "file" ? onRemoveFile(row.id) : onRemoveUrlItem(row.id);

  return (
    <>
      <div className="grid grid-cols-1 gap-3 sm:hidden">
        {rows.map((row, rowIndex) => {
          const isFile = row.kind === "file";
          const isUrl = row.kind === "url";
          const isInvalidFile = isFile && !row.isValid;
          const isUploadingFile = isFile && row.percent > 0 && row.percent < 100;
          const sourceLockable = disabled || row.isUploaded || (isFile && row.percent > 0);
          const rowError = perFileErrors?.[row.id];

          const mobileErrorId = rowError ? `target-error-${row.id}-mobile` : undefined;
          const rowErrorEntry = rowError
            ? TRANSLATE_ROW_ERROR_MESSAGE[rowError as TranslateRowError]
            : undefined;
          const targetErrorMessage = rowErrorEntry
            ? t(rowErrorEntry.i18nKey, rowErrorEntry.fallback)
            : rowError
              ? t("UPLOAD.ERROR.UNKNOWN", "Something went wrong. Try again.")
              : undefined;

          return (
            <Card key={row.id} className="p-3">
              <div className="flex items-start gap-2 min-w-0">
                <span className="mt-0.5 shrink-0">{getFileIcon(row.mediaCategory, isUrl, row.kind === "url" ? row.platform : null)}</span>
                <div className="flex-1 min-w-0" data-sentry-mask>
                  <p className="truncate text-sm font-medium text-foreground">{row.fileName}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {row.sizeBytes > 0 ? formatFileSize(row.sizeBytes) : isUrl ? t("UPLOAD_FILE.URL_SOURCE", "URL import") : ""}
                  </p>
                  {isInvalidFile && (
                    <p role="alert" className="mt-0.5 text-xs text-danger">
                      {row.errorMessage || "Error"}
                    </p>
                  )}
                </div>
                <div className="shrink-0 ml-auto pl-1">
                  {isInvalidFile && onRetry && isFile ? (
                    <div className="flex items-center gap-1.5">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={(e) => { e.stopPropagation(); onRetry(row.id); }}
                        disabled={disabled}
                        data-testid="file-retry-button-mobile"
                      >
                        <RotateCw aria-hidden="true" className="mr-1 h-3 w-3" />
                        {t("UPLOAD.RETRY", "Retry")}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); handleRemove(row); }}
                        disabled={disabled}
                        data-testid="file-skip-button-mobile"
                        aria-label={t("UPLOAD.SKIP_FILE_LABEL", "Skip {{fileName}}", { fileName: row.fileName })}
                      >
                        {t("ACTION.SKIP")}
                      </Button>
                    </div>
                  ) : !row.isUploaded && !isUploadingFile ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); handleRemove(row); }}
                      disabled={disabled}
                      aria-label={`${t("ACTION.REMOVE", "Remove")} ${row.fileName}`}
                      className="h-7 w-7 p-0"
                    >
                      <X className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  ) : null}
                </div>
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                {row.duration > 0 && (
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      {t("UPLOAD_FILE.DURATION_HEADER", "Duration")}
                    </span>
                    <span className="text-xs text-foreground">{formatDuration(row.duration)}</span>
                  </div>
                )}
                {creditMode && tierItems?.[rowIndex] && (
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      {t("UPLOAD_FILE.CONFIRM_COST", "Cost")}
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <span className="text-xs text-foreground tabular-nums">{formatCredits(tierItems[rowIndex].credits)}</span>
                      <TierBadge tier={tierItems[rowIndex].tier} reason={tierReason(tierItems[rowIndex].tier, tierItems[rowIndex].approximate)} />
                    </span>
                  </div>
                )}
                {!creditMode && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    {t("UPLOAD_FILE.CONFIRM_COST", "Cost")}
                  </span>
                  <span className="text-xs">
                    {isUploadingFile && isFile ? (
                      <span className="flex items-center gap-1.5">
                        <span className="inline-block h-1.5 w-12 rounded-full bg-muted overflow-hidden">
                          <span
                            role="progressbar"
                            aria-valuenow={row.percent}
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-label={t("UPLOAD_FILE.UPLOADING_FILE", "Uploading {{fileName}}", { fileName: row.fileName })}
                            className="block h-full rounded-full bg-primary transition-all motion-safe:transition-all"
                            style={{ width: `${row.percent}%` }}
                          />
                        </span>
                        <span className="text-muted-foreground" aria-hidden="true">{row.percent}%</span>
                      </span>
                    ) : row.isUploaded ? (
                      <CircleCheck className="h-4 w-4 text-success" aria-hidden="true" />
                    ) : row.amount > 0 ? (
                      <span className="text-muted-foreground">${row.amount.toFixed(2)}</span>
                    ) : (
                      <span className="text-success">{t("UPLOAD_FILE.FREE", "Free")}</span>
                    )}
                  </span>
                </div>
                )}
              </div>

              {sourceLanguages.length > 0 && (
                <div className="mt-3">
                  <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground block mb-1">
                    {isTranslateMode
                      ? t("TRANSLATE.SOURCE_LANGUAGE_HEADER", "Source language")
                      : t("UPLOAD_FILE.LANGUAGE_HEADER", "Language")}
                    <span className="text-destructive ml-0.5" aria-hidden="true">*</span>
                  </span>
                  <Combobox
                    value={row.sourceLanguage || ""}
                    onChange={(value) => handleLanguageChange(row, value)}
                    disabled={sourceLockable}
                    aria-label={`${t("UPLOAD_FILE.LANGUAGE_FOR", "Language for")} ${row.fileName}`}
                    data-testid={`upload-file-language-select-${row.id}-mobile`}
                    size="sm"
                    options={sourceLanguages.map((l) => ({ value: l.code, label: l.label }))}
                  />
                </div>
              )}

              {isTranslateMode && targetLanguages.length > 0 && (
                <div className="mt-3">
                  <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground block mb-1">
                    {t("UPLOAD_FILE.TARGET_LANGUAGE_HEADER", "Target language")}
                    <span className="text-destructive ml-0.5" aria-hidden="true">*</span>
                  </span>
                  <div
                    role="group"
                    aria-describedby={mobileErrorId}
                    className="flex flex-col gap-1"
                  >
                    <Combobox
                      value={row.targetLanguage || ""}
                      onChange={(value) => handleTargetLanguageChange(row, value)}
                      disabled={sourceLockable}
                      aria-label={t("UPLOAD_FILE.TARGET_LANGUAGE_FOR", "Target language for {{fileName}}", { fileName: row.fileName })}
                      error={!!rowError}
                      data-testid={`upload-file-target-language-select-${row.id}-mobile`}
                      size="sm"
                      placeholder={t("UPLOAD_FILE.SELECT_LANGUAGE", "Select language")}
                      options={targetLanguages.map((l) => ({ value: l.code, label: l.label }))}
                    />
                    {targetErrorMessage && (
                      <p
                        id={mobileErrorId}
                        role="alert"
                        className="text-xs text-danger"
                        data-testid={`upload-file-target-language-error-${row.id}-mobile`}
                      >
                        {targetErrorMessage}
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div className="mt-3">
                <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground block mb-1">
                  {t("UPLOAD_FILE.FOLDER_HEADER", "Folder")}
                  <span className="text-destructive ml-0.5" aria-hidden="true">*</span>
                </span>
                <Combobox
                  value={row.folderId || ""}
                  onChange={(value) => handleFolderChange(row, value)}
                  disabled={sourceLockable}
                  aria-label={t("UPLOAD_FILE.FOLDER_FOR_FILE", "Folder for {{fileName}}", { fileName: row.fileName })}
                  data-testid={`upload-file-folder-select-${row.id}-mobile`}
                  size="sm"
                  options={[
                    { value: "", label: t("UPLOAD_FILE.DEFAULT_FOLDER", "Default folder") },
                    ...folders.map((folder) => ({ value: folder.folderId, label: folder.name })),
                  ]}
                />
              </div>

              {isTranslateMode && (
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    {t("UPLOAD_FILE.DUBBING_HEADER", "Dubbing")}
                  </span>
                  {dubbingGateBlocked ? (
                    <div
                      className="inline-flex items-center gap-2 text-xs text-muted-foreground"
                      data-testid={`upload-file-dubbing-gated-${row.id}-mobile`}
                      aria-label={t(
                        "TRANSLATE.DUBBING_GATED_FOR",
                        "Dubbing for {{fileName}} (locked, add balance to enable)",
                        { fileName: row.fileName }
                      )}
                    >
                      <Lock className="h-3.5 w-3.5" aria-hidden="true" />
                      <span>{t("TRANSLATE.DUBBING_GATE_INLINE", "Add credit")}</span>
                    </div>
                  ) : (
                    <Switch
                      checked={!!row.enableDubbing}
                      onChange={(checked) => handleDubbingChange(row, checked)}
                      disabled={sourceLockable}
                      aria-label={t("UPLOAD_FILE.DUBBING_FOR", "Dubbing for {{fileName}}", { fileName: row.fileName })}
                      data-testid={`upload-file-dubbing-switch-${row.id}-mobile`}
                    />
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      <div className="hidden sm:block rounded-lg border border-border overflow-x-auto">
      <table className="w-full text-sm" aria-label={t("UPLOAD_FILE.FILE_LIST_CAPTION", "Selected files for upload")}>
        <caption className="sr-only">{t("UPLOAD_FILE.FILE_LIST_CAPTION", "Selected files for upload")}</caption>
        <thead className="bg-muted/50">
          <tr>
            <th scope="col" className="w-8 px-3 py-2" aria-hidden="true"></th>
            <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">{t("UPLOAD_FILE.FILE_HEADER", "File")}</th>
            <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">{t("UPLOAD_FILE.DURATION_HEADER", "Duration")}</th>
            <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">
              {t("UPLOAD_FILE.CONFIRM_COST", "Cost")}
            </th>
            {sourceLanguages.length > 0 && (
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                {mode === "translate"
                  ? t("TRANSLATE.SOURCE_LANGUAGE_HEADER", "Source language")
                  : t("UPLOAD_FILE.LANGUAGE_HEADER", "Language")}
                <span className="text-destructive ml-0.5" aria-hidden="true">*</span>
              </th>
            )}
            {isTranslateMode && targetLanguages.length > 0 && (
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                {t("UPLOAD_FILE.TARGET_LANGUAGE_HEADER", "Target language")}
                <span className="text-destructive ml-0.5" aria-hidden="true">*</span>
              </th>
            )}
            <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
              {t("UPLOAD_FILE.FOLDER_HEADER", "Folder")}
              <span className="text-destructive ml-0.5" aria-hidden="true">*</span>
            </th>
            {isTranslateMode && (
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                {t("UPLOAD_FILE.DUBBING_HEADER", "Dubbing")}
              </th>
            )}
            <th scope="col" className="w-8 px-3 py-2" aria-hidden="true"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((row, rowIndex) => {
            const isFile = row.kind === "file";
            const isUrl = row.kind === "url";
            const isInvalidFile = isFile && !row.isValid;
            const isUploadingFile = isFile && row.percent > 0 && row.percent < 100;
            const sourceLockable = disabled || row.isUploaded || (isFile && row.percent > 0);
            const rowError = perFileErrors?.[row.id];

            return (
              <tr key={row.id} className="bg-background hover:bg-muted/20 transition-colors">
                <td className="px-3 py-2.5">{getFileIcon(row.mediaCategory, isUrl, row.kind === "url" ? row.platform : null)}</td>
                <td className="px-3 py-2.5 min-w-0 max-w-[180px] lg:max-w-[320px] xl:max-w-[480px]" data-sentry-mask>
                  <p className="truncate font-medium text-foreground">{row.fileName}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {row.sizeBytes > 0 ? formatFileSize(row.sizeBytes) : isUrl ? t("UPLOAD_FILE.URL_SOURCE", "URL import") : ""}
                  </p>
                  {isInvalidFile && (
                    <p
                      role="alert"
                      className="mt-0.5 text-xs text-danger"
                    >
                      {row.errorMessage || "Error"}
                    </p>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap text-xs text-muted-foreground">
                  {row.duration > 0 ? formatDuration(row.duration) : "—"}
                </td>
                {creditMode && (
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    {tierItems?.[rowIndex] ? (
                      <span className="inline-flex items-center justify-end gap-2">
                        <span className="text-xs text-foreground tabular-nums">{formatCredits(tierItems[rowIndex].credits)}</span>
                        <TierBadge tier={tierItems[rowIndex].tier} reason={tierReason(tierItems[rowIndex].tier, tierItems[rowIndex].approximate)} />
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                )}
                {!creditMode && (
                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                  {isUploadingFile && isFile ? (
                    <div className="flex items-center justify-end gap-1.5">
                      <div className="h-1.5 w-12 rounded-full bg-muted">
                        <div
                          role="progressbar"
                          aria-valuenow={row.percent}
                          aria-valuemin={0}
                          aria-valuemax={100}
                          aria-label={t("UPLOAD_FILE.UPLOADING_FILE", "Uploading {{fileName}}", { fileName: row.fileName })}
                          className="h-full rounded-full bg-primary transition-all motion-safe:transition-all"
                          style={{ width: `${row.percent}%` }}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground" aria-hidden="true">{row.percent}%</span>
                    </div>
                  ) : row.isUploaded ? (
                    <CircleCheck className="ml-auto h-4 w-4 text-success" aria-hidden="true" />
                  ) : row.amount > 0 ? (
                    <span className="text-xs text-muted-foreground">${row.amount.toFixed(2)}</span>
                  ) : (
                    <span className="text-xs text-success">{t("UPLOAD_FILE.FREE", "Free")}</span>
                  )}
                </td>
                )}
                {sourceLanguages.length > 0 && (
                  <td className="px-3 py-2.5">
                    <div className="min-w-[140px]">
                      <Combobox
                        value={row.sourceLanguage || ""}
                        onChange={(value) => handleLanguageChange(row, value)}
                        disabled={sourceLockable}
                        aria-label={`${t("UPLOAD_FILE.LANGUAGE_FOR", "Language for")} ${row.fileName}`}
                        data-testid={`upload-file-language-select-${row.id}`}
                        size="sm"
                        options={sourceLanguages.map((l) => ({ value: l.code, label: l.label }))}
                      />
                    </div>
                  </td>
                )}
                {isTranslateMode && targetLanguages.length > 0 && (
                  <td className="px-3 py-2.5">
                    {(() => {
                      const errorId = rowError ? `target-error-${row.id}` : undefined;
                      const rowErrorEntry = rowError
                        ? TRANSLATE_ROW_ERROR_MESSAGE[rowError as TranslateRowError]
                        : undefined;
                      const errorMessage = rowErrorEntry
                        ? t(rowErrorEntry.i18nKey, rowErrorEntry.fallback)
                        : rowError
                          ? t("UPLOAD.ERROR.UNKNOWN", "Something went wrong. Try again.")
                          : undefined;
                      return (
                        <div
                          role="group"
                          aria-describedby={errorId}
                          className="min-w-[140px] flex flex-col gap-1"
                        >
                          <Combobox
                            value={row.targetLanguage || ""}
                            onChange={(value) => handleTargetLanguageChange(row, value)}
                            disabled={sourceLockable}
                            aria-label={t("UPLOAD_FILE.TARGET_LANGUAGE_FOR", "Target language for {{fileName}}", { fileName: row.fileName })}
                            error={!!rowError}
                            data-testid={`upload-file-target-language-select-${row.id}`}
                            size="sm"
                            placeholder={t("UPLOAD_FILE.SELECT_LANGUAGE", "Select language")}
                            options={targetLanguages.map((l) => ({ value: l.code, label: l.label }))}
                          />
                          {errorMessage && (
                            <p
                              id={errorId}
                              role="alert"
                              className="text-xs text-danger"
                              data-testid={`upload-file-target-language-error-${row.id}`}
                            >
                              {errorMessage}
                            </p>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                )}
                <td className="px-3 py-2.5">
                  <div className="min-w-[150px]">
                    <Combobox
                      value={row.folderId || ""}
                      onChange={(value) => handleFolderChange(row, value)}
                      disabled={sourceLockable}
                      aria-label={t("UPLOAD_FILE.FOLDER_FOR_FILE", "Folder for {{fileName}}", { fileName: row.fileName })}
                      data-testid={`upload-file-folder-select-${row.id}`}
                      size="sm"
                      options={[
                        { value: "", label: t("UPLOAD_FILE.DEFAULT_FOLDER", "Default folder") },
                        ...folders.map((folder) => ({ value: folder.folderId, label: folder.name })),
                      ]}
                    />
                  </div>
                </td>
                {isTranslateMode && (
                  <td className="px-3 py-2.5">
                    {dubbingGateBlocked ? (
                      <div
                        className="inline-flex items-center gap-2 text-xs text-muted-foreground"
                        data-testid={`upload-file-dubbing-gated-${row.id}`}
                        aria-label={t(
                          "TRANSLATE.DUBBING_GATED_FOR",
                          "Dubbing for {{fileName}} (locked, add balance to enable)",
                          { fileName: row.fileName }
                        )}
                      >
                        <Lock className="h-3.5 w-3.5" aria-hidden="true" />
                        <span>
                          {t(
                            "TRANSLATE.DUBBING_GATE_INLINE",
                            "Add credit"
                          )}
                        </span>
                      </div>
                    ) : (
                      <Switch
                        checked={!!row.enableDubbing}
                        onChange={(checked) => handleDubbingChange(row, checked)}
                        disabled={sourceLockable}
                        aria-label={t("UPLOAD_FILE.DUBBING_FOR", "Dubbing for {{fileName}}", { fileName: row.fileName })}
                        data-testid={`upload-file-dubbing-switch-${row.id}`}
                      />
                    )}
                  </td>
                )}
                <td className="px-3 py-2.5">
                  {isInvalidFile && onRetry && isFile ? (
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={(e) => { e.stopPropagation(); onRetry(row.id); }}
                        disabled={disabled}
                        data-testid="file-retry-button"
                      >
                        <RotateCw aria-hidden="true" className="mr-1 h-3 w-3" />
                        {t("UPLOAD.RETRY", "Retry")}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); handleRemove(row); }}
                        disabled={disabled}
                        data-testid="file-skip-button"
                        aria-label={t("UPLOAD.SKIP_FILE_LABEL", "Skip {{fileName}}", { fileName: row.fileName })}
                      >
                        {t("ACTION.SKIP")}
                      </Button>
                    </div>
                  ) : !row.isUploaded && !isUploadingFile && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); handleRemove(row); }}
                      disabled={disabled}
                      aria-label={`${t("ACTION.REMOVE", "Remove")} ${row.fileName}`}
                      className="h-7 w-7 p-0"
                    >
                      <X className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </>
  );
}
